import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { usersDB } from '../lib/db/users';
import { schoolsDB } from '../lib/db/schools';
import { getTerminologyTerm } from '../lib/terminology';
import { trackEvent, EVENTS } from '../lib/analytics';
import { requestNotificationPermission } from '../lib/notifications';
import { 
  requestPushPermission, 
  unsubscribeFromPush, 
  isPushSupported 
} from '../lib/push-notifications';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import Modal from '../components/common/Modal';
import { TRACKS, getBeltsForTrack, getDefaultBeltForTrack, normalizeTrack } from '../lib/track';

export default function Settings() {
  const [userData, setUserData] = useState(null);
  const [schoolData, setSchoolData] = useState(null);
  const [belts, setBelts] = useState([]);
  const [name, setName] = useState('');
  const [currentBelt, setCurrentBelt] = useState('');
  const [track, setTrack] = useState(TRACKS.ADULT);
  const [dailyGoal, setDailyGoal] = useState(10);
  const [reminderTime, setReminderTime] = useState('18:00');
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pushPermission, setPushPermission] = useState('default');
  const [modalState, setModalState] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'alert', variant: 'primary' });
  const [buildInfo, setBuildInfo] = useState({ version: '', buildDate: '', buildTime: '' });
  const [isStaff, setIsStaff] = useState(false);
  
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  // Refs to store latest values for cleanup effect
  const userDataRef = useRef(userData);
  const currentUserRef = useRef(currentUser);
  const savingRef = useRef(saving);
  const nameRef = useRef(name);
  const currentBeltRef = useRef(currentBelt);
  const trackRef = useRef(track);
  const dailyGoalRef = useRef(dailyGoal);
  const reminderTimeRef = useRef(reminderTime);
  const reminderEnabledRef = useRef(reminderEnabled);
  
  // Update refs when values change
  useEffect(() => {
    userDataRef.current = userData;
    currentUserRef.current = currentUser;
    savingRef.current = saving;
    nameRef.current = name;
    currentBeltRef.current = currentBelt;
    trackRef.current = track;
    dailyGoalRef.current = dailyGoal;
    reminderTimeRef.current = reminderTime;
    reminderEnabledRef.current = reminderEnabled;
  }, [userData, currentUser, saving, name, currentBelt, track, dailyGoal, reminderTime, reminderEnabled]);
  
  const showModal = (title, message, onConfirm = null, type = 'alert', variant = 'primary') => {
    setModalState({ isOpen: true, title, message, onConfirm, type, variant });
  };
  
  const hideModal = () => {
    setModalState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'alert', variant: 'primary' });
  };
  
  const handleModalConfirm = () => {
    if (modalState.onConfirm) {
      modalState.onConfirm();
    }
    hideModal();
  };
  
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const user = await usersDB.getUserData(currentUser.uid);
      setUserData(user);
      setName(user.profile.name);
      setDailyGoal(user.settings.dailyGoalMinutes);
      setReminderTime(user.settings.reminderTime);
      setReminderEnabled(user.settings.reminderEnabled);
      
      const [schoolBelts, school, tokenResult] = await Promise.all([
        schoolsDB.getBelts(user.profile.schoolId),
        schoolsDB.getSchool(user.profile.schoolId),
        currentUser.getIdTokenResult()
      ]);
      setBelts(schoolBelts);
      setSchoolData(school);

      const userTrack = normalizeTrack(user.profile.track);
      const beltsForTrack = getBeltsForTrack(schoolBelts, userTrack);
      const beltForTrack = beltsForTrack.some((b) => b.id === user.profile.currentBelt)
        ? user.profile.currentBelt
        : getDefaultBeltForTrack(userTrack);

      setTrack(userTrack);
      setCurrentBelt(beltForTrack);

      const isSuperAdmin = tokenResult.claims.role === 'super-admin';
      const isSchoolStaff = (school?.adminIds || []).includes(currentUser.uid) ||
                            (school?.instructorIds || []).includes(currentUser.uid);
      setIsStaff(isSuperAdmin || isSchoolStaff);
      
      // Check push notification support and permission
      if (isPushSupported()) {
        setPushPermission(Notification.permission);
      }
      
      trackEvent(EVENTS.SETTINGS_VIEWED);
    } catch (err) {
      console.error('Error fetching settings data:', err);
      setError(err);
      trackEvent(EVENTS.ERROR_OCCURRED, {
        errorType: 'fetch_settings_data',
        errorMessage: err.message
      });
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [currentUser]);

  useEffect(() => {
    const storageKey = 'appBuildInfo';
    let storedInfo = null;

    try {
      const rawInfo = localStorage.getItem(storageKey);
      if (rawInfo) {
        storedInfo = JSON.parse(rawInfo);
        if (storedInfo && typeof storedInfo === 'object') {
          setBuildInfo((prev) => ({ ...prev, ...storedInfo }));
        }
      }
    } catch (err) {
      console.error('Error reading build info from storage:', err);
    }

    if (!storedInfo) {
      const loadBuildInfo = async () => {
        try {
          const response = await fetch('/build-info.json');
          if (response.ok) {
            const info = await response.json();
            setBuildInfo(info);
            try {
              localStorage.setItem(storageKey, JSON.stringify(info));
            } catch (storageError) {
              // ignore storage write errors
            }
          }
        } catch (err) {
          console.error('Error loading build info:', err);
        }
      };
      loadBuildInfo();
    }
  }, []);

  // Save settings when component unmounts (cleanup)
  useEffect(() => {
    return () => {
      // Save on unmount if userData is loaded and not already saving
      if (userDataRef.current && currentUserRef.current && !savingRef.current) {
        const saveOnUnmount = async () => {
          try {
            await usersDB.updateProfile(currentUserRef.current.uid, {
              name: nameRef.current,
              currentBelt: currentBeltRef.current,
              track: trackRef.current,
              language: i18n.language
            });
            
            await usersDB.updateSettings(currentUserRef.current.uid, {
              dailyGoalMinutes: parseInt(dailyGoalRef.current),
              reminderTime: reminderTimeRef.current,
              reminderEnabled: reminderEnabledRef.current
            });
            
            if (userDataRef.current && currentBeltRef.current !== userDataRef.current.profile.currentBelt) {
              trackEvent(EVENTS.BELT_CHANGED, {
                oldBelt: userDataRef.current.profile.currentBelt,
                newBelt: currentBeltRef.current
              });
            }
            
            trackEvent(EVENTS.SETTINGS_UPDATED);
          } catch (err) {
            console.error('Error saving settings on unmount:', err);
          }
        };
        saveOnUnmount();
      }
    };
  }, [i18n.language]);
  
  const saveSettings = async (showSuccessModal = false) => {
    if (saving) return; // Prevent multiple simultaneous saves
    
    setSaving(true);
    
    try {
      await usersDB.updateProfile(currentUser.uid, {
        name,
        currentBelt,
        track,
        language: i18n.language
      });
      
      await usersDB.updateSettings(currentUser.uid, {
        dailyGoalMinutes: parseInt(dailyGoal),
        reminderTime,
        reminderEnabled
      });
      
      if (userData && currentBelt !== userData.profile.currentBelt) {
        trackEvent(EVENTS.BELT_CHANGED, {
          oldBelt: userData.profile.currentBelt,
          newBelt: currentBelt
        });
      }
      
      trackEvent(EVENTS.SETTINGS_UPDATED);
      
      if (showSuccessModal) {
        showModal(t('settings.saved'), '', () => navigate('/home'), 'alert', 'success');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      trackEvent(EVENTS.ERROR_OCCURRED, {
        errorType: 'save_settings',
        errorMessage: err.message
      });
      if (showSuccessModal) {
        showModal(t('settings.error'), err.message, null, 'alert', 'danger');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBack = async () => {
    await saveSettings(false);
    navigate('/home');
  };

  const handleTrackChange = (newTrack) => {
    const normalized = normalizeTrack(newTrack);
    setTrack(normalized);
    const trackBelts = getBeltsForTrack(belts, normalized);
    if (!trackBelts.some((b) => b.id === currentBelt)) {
      setCurrentBelt(getDefaultBeltForTrack(normalized));
    }
  };

  const trackBelts = getBeltsForTrack(belts, track);
  
  const handleEnableReminders = async () => {
    try {
      if (reminderEnabled) {
        // Disabling
        await unsubscribeFromPush();
        await usersDB.savePushSubscription(currentUser.uid, null);
        setReminderEnabled(false);
      } else {
        // Enabling
        const subscription = await requestPushPermission();
        if (subscription) {
          await usersDB.savePushSubscription(currentUser.uid, subscription);
          setReminderEnabled(true);
          setPushPermission('granted');
        } else {
          showModal('Unable to enable notifications', 'Please check your browser settings.', null, 'alert', 'warning');
        }
      }
    } catch (err) {
      console.error('Error handling reminders:', err);
      trackEvent(EVENTS.ERROR_OCCURRED, {
        errorType: 'handle_reminders',
        errorMessage: err.message
      });
      showModal(t('settings.error'), err.message, null, 'alert', 'danger');
    }
  };

  const handleRequestNotificationPermission = async () => {
    try {
      if (!('Notification' in window)) {
        showModal(t('settings.notifications_not_supported'), t('settings.notifications_not_supported_message') || '', null, 'alert', 'warning');
        return;
      }

      // Check if we're on HTTPS or localhost
      const isSecureContext = window.isSecureContext || window.location.hostname === 'localhost';
      if (!isSecureContext) {
        // For development, show a helpful message about enabling notifications
        const enableNotifications = confirm(t('settings.notifications_https_required') + '\n\n' + t('settings.enable_notifications_manually'));
        if (enableNotifications) {
          setReminderEnabled(true);
          trackEvent(EVENTS.NOTIFICATION_PERMISSION_GRANTED);
        } else {
          setReminderEnabled(false);
        }
        return;
      }

      const granted = await requestNotificationPermission();
      
      if (granted) {
        setReminderEnabled(true);
        trackEvent(EVENTS.NOTIFICATION_PERMISSION_GRANTED);
      } else {
        showModal(t('settings.notifications_denied'), t('settings.notifications_denied_message') || '', null, 'alert', 'warning');
        setReminderEnabled(false);
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      trackEvent(EVENTS.ERROR_OCCURRED, {
        errorType: 'request_notification_permission',
        errorMessage: error.message
      });
      showModal(t('settings.notifications_error'), error.message, null, 'alert', 'danger');
      setReminderEnabled(false);
    }
  };
  
  if (loading) {
    return <LoadingSpinner message={t('common.loading')} />;
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 p-4">
        <div className="max-w-2xl mx-auto">
          <ErrorMessage 
            error={error} 
            onRetry={fetchData}
            className="mt-8"
          />
        </div>
      </div>
    );
  }
  
  if (!userData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 p-4">
        <div className="max-w-2xl mx-auto">
          <ErrorMessage 
            error={{ message: t('error.not_found') }} 
            onRetry={fetchData}
            className="mt-8"
          />
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header with Back Button */}
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={handleBack}
            className="text-2xl hover:bg-gray-100 rounded-full p-2 transition-colors"
            title={t('common.back')}
          >
            {i18n.language === 'he' ? '→' : '←'}
          </button>
          <h1 className="text-xl md:text-5xl font-bold bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent">
            {t('settings.title')}
          </h1>
        </div>
        
        <div className="bg-white rounded-2xl p-4 md:p-10 shadow-xl space-y-5 md:space-y-8 animate-slide-up">
          {/* Profile - Inline labels and inputs */}
          <div>
            <h2 className="font-bold mb-3 md:mb-5 text-lg md:text-2xl text-gray-800">{t('settings.profile')}</h2>
            
            <div className="space-y-3 md:space-y-4">
              <div>
                <label className="block text-sm md:text-lg font-semibold mb-2 text-gray-700">
                  {t('settings.name')}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input text-sm md:text-xl h-12"
                />
              </div>

              <div>
                <label className="block text-sm md:text-lg font-semibold mb-2 text-gray-700">
                  {t('settings.track')}
                </label>
                <div className="flex gap-2">
                  {[TRACKS.ADULT, TRACKS.YOUTH].map((trackOption) => (
                    <button
                      key={trackOption}
                      type="button"
                      onClick={() => handleTrackChange(trackOption)}
                      className={`flex-1 px-4 py-3 rounded-xl text-sm md:text-lg font-semibold transition-colors min-h-[48px] ${
                        track === trackOption
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                      }`}
                    >
                      {t(`settings.track_${trackOption}`)}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm md:text-lg font-semibold mb-2 text-gray-700">
                  {getTerminologyTerm(schoolData, 'belt', i18n.language)
                    ? t('settings.current_belt_label', { label: getTerminologyTerm(schoolData, 'belt', i18n.language) })
                    : t('settings.current_belt')}
                </label>
                <select
                  value={currentBelt}
                  onChange={(e) => setCurrentBelt(e.target.value)}
                  disabled={!!schoolData?.settings?.onlyTeacherSetsBelt}
                  className={`input text-sm md:text-xl h-12 ${schoolData?.settings?.onlyTeacherSetsBelt ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {trackBelts.map(belt => (
                    <option key={belt.id} value={belt.id}>
                      {belt[`name${i18n.language === 'he' ? 'He' : 'En'}`]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          {/* Goals - Inline input with "minutes" suffix */}
          <div>
            <h2 className="font-bold mb-3 md:mb-5 text-lg md:text-2xl text-gray-800">{t('settings.goals')}</h2>
            
            <div>
              <label className="block text-sm md:text-lg font-semibold mb-2 text-gray-700">
                {t('settings.daily_goal')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={dailyGoal}
                  onChange={(e) => setDailyGoal(e.target.value)}
                  className="input text-sm md:text-xl h-12 flex-1"
                  min="1"
                  max="120"
                />
                <span className="text-sm md:text-lg text-gray-600 whitespace-nowrap">
                  {t('settings.daily_goal_minutes')}
                </span>
              </div>
            </div>
          </div>
          
          {/* Reminders */}
          <div>
            <h2 className="font-bold mb-3 md:mb-5 text-lg md:text-2xl text-gray-800">{t('settings.reminders')}</h2>
            
            <div className="space-y-3 md:space-y-4">
              <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-xl">
                <input
                  type="checkbox"
                  id="reminder-checkbox"
                  checked={reminderEnabled}
                  onChange={handleEnableReminders}
                  className="w-5 h-5 md:w-6 md:h-6 cursor-pointer rounded"
                />
                <label 
                  htmlFor="reminder-checkbox" 
                  className="cursor-pointer select-none text-sm md:text-lg font-medium"
                >
                  {t('settings.enable_reminder')}
                </label>
              </div>
              
              {!isPushSupported() && (
                <p style={{ color: 'orange' }} className="text-xs md:text-sm p-2 bg-orange-50 rounded-lg">
                  {t('settings.push_notifications_not_supported')}
                </p>
              )}
              {pushPermission === 'denied' && (
                <p style={{ color: 'red' }} className="text-xs md:text-sm p-2 bg-red-50 rounded-lg">
                  {t('settings.notifications_blocked')}
                </p>
              )}
              
              {reminderEnabled && (
                <div>
                  <label className="block text-sm md:text-lg font-semibold mb-2 text-gray-700">
                    {t('settings.reminder_time')}
                  </label>
                  <input
                    type="time"
                    value={reminderTime}
                    onChange={(e) => setReminderTime(e.target.value)}
                    className="input text-sm md:text-xl h-12"
                  />
                </div>
              )}
              
              <div className="text-xs md:text-sm text-gray-600 bg-blue-50 p-2 rounded-lg">
                ℹ️ {t('settings.quiet_hours_note')}
              </div>
            </div>
          </div>
          
          {/* Language */}
          <div>
            <h2 className="font-bold mb-3 md:mb-5 text-lg md:text-2xl text-gray-800">{t('settings.language')}</h2>
            <select
              value={i18n.language}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              className="input text-sm md:text-xl h-12"
            >
              <option value="he">עברית</option>
              <option value="en">English</option>
            </select>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col gap-3">
            {isStaff && (
              <button
                onClick={() => navigate('/admin')}
                className="w-full px-6 py-3 md:py-4 border-2 border-primary-300 rounded-xl hover:bg-primary-50 text-base md:text-xl h-12 md:h-16 flex items-center justify-center font-semibold transition-all hover:scale-105 active:scale-95 text-primary-700"
              >
                {t('settings.admin_panel', 'Admin Panel')}
              </button>
            )}
            <button
              onClick={logout}
              className="w-full px-6 py-3 md:py-4 border-2 border-gray-300 rounded-xl hover:bg-gray-50 text-base md:text-xl h-12 md:h-16 flex items-center justify-center font-semibold transition-all hover:scale-105 active:scale-95"
            >
              {t('common.logout')}
            </button>
          </div>

          {/* Version and Build Info */}
          <div className="pt-4 md:pt-6 border-t border-gray-200">
            <div className="text-xs md:text-sm text-gray-500 space-y-1">
              <div className="flex justify-between items-center">
                <span className="font-semibold">{t('settings.version')}:</span>
                <span>{buildInfo.version || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold">{t('settings.build')}:</span>
                <span>{buildInfo.buildTime || buildInfo.buildDate || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal for alerts */}
      <Modal
        isOpen={modalState.isOpen}
        title={modalState.title}
        message={modalState.message}
        onConfirm={handleModalConfirm}
        onCancel={hideModal}
        type={modalState.type}
        variant={modalState.variant}
      />
    </div>
  );
}